import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FileText, ArrowLeft, Mail, MessageCircle, ClipboardCheck, Send, ShoppingCart, Search, Trash2, XCircle, RotateCcw, Pencil, Clock, AlertTriangle } from 'lucide-react';
import { getAdminQuotes, getAdminQuote, updateQuoteStatus, convertQuoteToOrder, cancelQuote, deleteQuote, reopenQuote, editQuote } from '../../lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  new: 'bg-blue-500/20 text-blue-400',
  under_review: 'bg-yellow-500/20 text-yellow-400',
  quote_sent: 'bg-purple-500/20 text-purple-400',
  approved: 'bg-green-500/20 text-green-400',
  converted_to_order: 'bg-cyan-500/20 text-cyan-400',
  cancelled: 'bg-red-500/20 text-red-400',
};
const STATUS_LABELS = {
  new: 'New Request', under_review: 'Under Review', quote_sent: 'Quote Sent',
  approved: 'Approved', converted_to_order: 'Converted to Order', cancelled: 'Cancelled',
};

const ConfirmModal = ({ open, title, message, warning, onConfirm, onCancel, confirmLabel = 'Confirm', danger }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-white/60 text-sm mb-4">{message}</p>
        {warning && <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs mb-4"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{warning}</span></div>}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-white/60 hover:text-white" data-testid="confirm-cancel-btn">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium ${danger ? 'bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30' : 'bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30'}`} data-testid="confirm-action-btn">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchQuotes = async () => {
    try { setLoading(true); const res = await getAdminQuotes({ status: statusFilter || undefined }); setQuotes(res.data.quotes || []); setTotal(res.data.total || 0); } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchQuotes(); }, [statusFilter]);

  return (
    <div data-testid="admin-quotes-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold flex items-center gap-3"><FileText className="w-6 h-6 text-[#00D4FF]" />Quote Requests</h1><p className="text-white/60 mt-1">{total} total</p></div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 bg-[#0A0A0A] border border-white/10 text-white text-sm" data-testid="quote-status-filter">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {loading ? <div className="card-dark p-8 text-center text-white/40">Loading...</div> : quotes.length === 0 ? <div className="card-dark p-8 text-center text-white/40">No quote requests found</div> : (
        <div className="space-y-3">{quotes.map(q => (
          <Link key={q.quote_id} to={`/admin/quotes/${q.quote_id}`} className="card-dark p-4 flex items-center justify-between hover:border-[#00D4FF]/30 border border-transparent transition-colors block" data-testid={`quote-row-${q.quote_id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-mono text-[#00D4FF] text-sm font-semibold">{q.quote_id}</span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_COLORS[q.status] || 'bg-gray-500/20 text-gray-400'}`}>{STATUS_LABELS[q.status] || q.status}</span>
              </div>
              <p className="text-white/80 text-sm">{q.company_name} — {q.contact_name}</p>
              <p className="text-white/40 text-xs">{q.destination_country} | {q.item_count || q.items?.length} items | ${(q.total_amount || 0).toLocaleString()}</p>
            </div>
            <div className="text-white/40 text-xs text-right ml-4">{new Date(q.created_at).toLocaleDateString()}</div>
          </Link>
        ))}</div>
      )}
    </div>
  );
};

const AdminQuoteDetail = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const reload = () => getAdminQuote(quoteId).then(res => { setQuote(res.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { reload(); }, [quoteId]);

  const doAction = async (fn, successMsg) => {
    try { setActionLoading(true); await fn(); toast.success(successMsg); await reload(); } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); } finally { setActionLoading(false); setConfirm(null); }
  };

  const handleSaveEdit = async () => {
    await doAction(() => editQuote(quoteId, editForm), 'Quote updated');
    setEditing(false);
  };

  if (loading) return <div className="p-8 text-white/40 text-center">Loading...</div>;
  if (!quote) return <div className="p-8 text-white/40 text-center">Quote not found</div>;
  const s = quote.status;

  return (
    <div data-testid="admin-quote-detail">
      <button onClick={() => navigate('/admin/quotes')} className="flex items-center gap-2 text-white/60 hover:text-white mb-6 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Quotes</button>

      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">{quote.quote_id}</h1><p className="text-white/40 text-sm mt-1">{new Date(quote.created_at).toLocaleString()}</p></div>
        <span className={`px-3 py-1 text-xs font-bold uppercase ${STATUS_COLORS[s]}`}>{STATUS_LABELS[s] || s}</span>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="quote-actions">
        {s === 'new' && <button onClick={() => doAction(() => updateQuoteStatus(quoteId, { status: 'under_review' }), 'Marked Under Review')} disabled={actionLoading} className="flex items-center gap-2 px-3 py-2 bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-sm font-medium disabled:opacity-50" data-testid="btn-verify-inventory"><ClipboardCheck className="w-4 h-4" />Verify Inventory</button>}
        {s === 'under_review' && <button onClick={() => doAction(() => updateQuoteStatus(quoteId, { status: 'quote_sent' }), 'Quote Sent')} disabled={actionLoading} className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 border border-purple-500/40 text-purple-400 text-sm font-medium disabled:opacity-50" data-testid="btn-send-quote"><Send className="w-4 h-4" />Send Quote</button>}
        {s === 'quote_sent' && <button onClick={() => doAction(() => updateQuoteStatus(quoteId, { status: 'approved' }), 'Approved')} disabled={actionLoading} className="flex items-center gap-2 px-3 py-2 bg-green-500/20 border border-green-500/40 text-green-400 text-sm font-medium disabled:opacity-50" data-testid="btn-approve-quote"><ClipboardCheck className="w-4 h-4" />Mark Approved</button>}
        {s === 'approved' && <button onClick={() => setConfirm({ type: 'convert', title: 'Convert to Order', message: `Create an order from quote ${quoteId}? This will mark the quote as converted.`, confirmLabel: 'Convert to Order' })} disabled={actionLoading} className="flex items-center gap-2 px-3 py-2 bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] text-sm font-medium disabled:opacity-50" data-testid="btn-convert-to-order"><ShoppingCart className="w-4 h-4" />Convert to Order</button>}
        {s === 'converted_to_order' && quote.converted_order_number && <Link to="/admin/orders" className="flex items-center gap-2 px-3 py-2 bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 text-sm font-medium" data-testid="btn-view-order"><ShoppingCart className="w-4 h-4" />View Order {quote.converted_order_number}</Link>}

        {/* Edit */}
        {s !== 'converted_to_order' && !editing && <button onClick={() => { setEditing(true); setEditForm({ company_name: quote.company_name, contact_name: quote.contact_name, email: quote.email, notes: quote.notes || '' }); }} className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white/70 text-sm hover:text-white" data-testid="btn-edit-quote"><Pencil className="w-4 h-4" />Edit</button>}

        {/* Cancel */}
        {!['cancelled', 'converted_to_order'].includes(s) && <button onClick={() => setConfirm({ type: 'cancel', title: 'Cancel Quote', message: `Cancel quote ${quoteId}? It will be marked as cancelled but not deleted.`, confirmLabel: 'Cancel Quote', danger: true })} className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20" data-testid="btn-cancel-quote"><XCircle className="w-4 h-4" />Cancel</button>}

        {/* Reopen */}
        {['cancelled', 'converted_to_order'].includes(s) && <button onClick={() => setConfirm({ type: 'reopen', title: 'Reopen Quote', message: `Reopen quote ${quoteId} to Approved status?`, warning: s === 'converted_to_order' ? 'This quote was already converted to an order. Reopening will NOT delete the linked order.' : undefined, confirmLabel: 'Reopen' })} className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white/70 text-sm hover:text-white" data-testid="btn-reopen-quote"><RotateCcw className="w-4 h-4" />Reopen</button>}

        {/* Delete */}
        <button onClick={() => setConfirm({ type: 'delete', title: 'Delete Quote', message: `Permanently delete quote ${quoteId}? This cannot be undone.`, warning: quote.converted_order_number ? `Linked order ${quote.converted_order_number} will NOT be deleted.` : undefined, confirmLabel: 'Delete Forever', danger: true })} className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20" data-testid="btn-delete-quote"><Trash2 className="w-4 h-4" />Delete</button>
      </div>

      {/* Edit Form */}
      {editing && (
        <div className="card-dark p-5 mb-6 space-y-3" data-testid="quote-edit-form">
          <h3 className="font-semibold text-sm text-white/60 uppercase">Edit Quote</h3>
          {['company_name', 'contact_name', 'email'].map(f => (
            <div key={f}><label className="text-xs text-white/40 block mb-1">{f.replace('_', ' ')}</label>
            <input value={editForm[f] || ''} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" /></div>
          ))}
          <div><label className="text-xs text-white/40 block mb-1">Notes</label>
          <textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" rows={3} /></div>
          <div className="flex gap-2">
            <button onClick={handleSaveEdit} disabled={actionLoading} className="px-4 py-2 bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] text-sm font-medium" data-testid="btn-save-edit">Save</button>
            <button onClick={() => setEditing(false)} className="px-4 py-2 text-white/60 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="card-dark p-5">
          <h3 className="text-white/40 text-xs uppercase mb-3">Contact</h3>
          <p className="text-white font-medium">{quote.contact_name}</p>
          <p className="text-white/60 text-sm">{quote.company_name}</p>
          <div className="mt-3 space-y-1">
            <a href={`mailto:${quote.email}`} className="flex items-center gap-2 text-[#00D4FF] text-sm hover:underline"><Mail className="w-3 h-3" />{quote.email}</a>
            {quote.whatsapp && <a href={`https://wa.me/${quote.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#10B981] text-sm hover:underline"><MessageCircle className="w-3 h-3" />{quote.whatsapp}</a>}
          </div>
        </div>
        <div className="card-dark p-5">
          <h3 className="text-white/40 text-xs uppercase mb-3">Destination</h3>
          <p className="text-white font-medium">{quote.destination_country}</p>
          {quote.notes && <p className="text-white/60 text-sm mt-2">{quote.notes}</p>}
        </div>
        <div className="card-dark p-5">
          <h3 className="text-white/40 text-xs uppercase mb-3">Pipeline</h3>
          <div className="space-y-2 text-xs">
            {Object.entries(STATUS_LABELS).map(([k, label]) => (
              <div key={k} className={`flex items-center gap-2 ${s === k ? 'text-white font-bold' : 'text-white/30'}`}>
                <div className={`w-2 h-2 rounded-full ${s === k ? 'bg-[#00D4FF]' : 'bg-white/10'}`} />{label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="card-dark overflow-hidden mb-6">
        <div className="p-4 border-b border-white/10"><h3 className="font-semibold">{quote.items?.length} Items — ${(quote.total_amount || 0).toLocaleString()}</h3></div>
        <table className="w-full">
          <thead><tr className="bg-[#0A0A0A] border-b border-white/10 text-[11px] text-white/60 uppercase">
            <th className="px-4 py-3 text-left">Unique #</th><th className="px-4 py-3 text-left">Brand</th><th className="px-4 py-3 text-left">Model</th><th className="px-4 py-3 text-center">Grade</th><th className="px-4 py-3 text-center">Qty</th><th className="px-4 py-3 text-right">Unit Price</th>
          </tr></thead>
          <tbody>{(quote.items || []).map((item, idx) => (
            <tr key={idx} className="border-b border-white/5">
              <td className="px-4 py-3 font-mono text-[#00D4FF] text-xs">{item.unique_id}</td>
              <td className="px-4 py-3 text-sm">{item.oem}</td><td className="px-4 py-3 text-sm text-white/80">{item.model}</td>
              <td className="px-4 py-3 text-center text-xs">{item.grade || '-'}</td><td className="px-4 py-3 text-center">{item.qty}</td>
              <td className="px-4 py-3 text-right font-mono">${(item.unit_price || 0).toLocaleString()}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      {/* Timeline / Audit Trail */}
      {quote.history && quote.history.length > 0 && (
        <div className="card-dark p-5" data-testid="quote-timeline">
          <h3 className="text-white/40 text-xs uppercase mb-4 flex items-center gap-2"><Clock className="w-4 h-4" />Timeline</h3>
          <div className="space-y-3">
            {[...quote.history].reverse().map((h, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-[#00D4FF]/60 mt-1.5 shrink-0" />
                <div>
                  <span className="text-white font-medium">{h.action}</span>
                  {h.details && <span className="text-white/50"> — {h.details}</span>}
                  <div className="text-white/30 text-xs mt-0.5">{new Date(h.at).toLocaleString()} · {h.by}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        warning={confirm?.warning}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.type === 'convert') doAction(async () => { const res = await convertQuoteToOrder(quoteId); toast.success(res.data.message); }, '');
          else if (confirm?.type === 'cancel') doAction(() => cancelQuote(quoteId), 'Quote cancelled');
          else if (confirm?.type === 'delete') doAction(async () => { await deleteQuote(quoteId); navigate('/admin/quotes'); }, 'Quote deleted');
          else if (confirm?.type === 'reopen') doAction(() => reopenQuote(quoteId, { status: 'approved' }), 'Quote reopened');
        }}
      />
    </div>
  );
};

export { AdminQuotes, AdminQuoteDetail };
